import { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError } from "../../contexts/AuthContext";
import {
  Search,
  Download,
  RefreshCcw,
  Eye,
  Trash2,
  X,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Filter,
  Check,
} from "lucide-react";

const CATEGORIES = ["all", "pilgrim", "volunteer", "vendor", "media"];
const STATUSES = ["all", "pending", "approved", "rejected", "confirmed"];
const STATUS_OPTIONS = ["pending", "approved", "rejected", "confirmed"];

const statusBadge = {
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  approved: "bg-blue-100 text-blue-700 border-blue-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
  confirmed: "bg-green-100 text-green-700 border-green-200",
};

const categoryBadge = {
  pilgrim: "bg-amber-50 text-amber-700",
  volunteer: "bg-emerald-50 text-emerald-700",
  vendor: "bg-violet-50 text-violet-700",
  media: "bg-sky-50 text-sky-700",
};

export default function Registrations() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dateField, setDateField] = useState("created_at");
  const [viewing, setViewing] = useState(null);
  const [error, setError] = useState("");
  const [actionMsg, setActionMsg] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { page, limit };
      if (search) params.search = search;
      if (category !== "all") params.category = category;
      if (status !== "all") params.status = status;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (dateFrom || dateTo) params.date_field = dateField;
      const { data } = await adminApi.get("/admin/registrations", { params });
      setRows(data.registrations || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, category, status, dateFrom, dateTo, dateField]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setCategory("all");
    setStatus("all");
    setDateFrom("");
    setDateTo("");
    setDateField("created_at");
    setPage(1);
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await adminApi.patch(`/admin/registrations/${id}`, { status: newStatus });
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status: newStatus } : r)));
      setActionMsg(`Status updated to "${newStatus}"`);
      setTimeout(() => setActionMsg(""), 2500);
    } catch (e) {
      alert("Failed to update status: " + formatApiError(e));
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete registration for "${name}"? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/registrations/${id}`);
      setRows((prev) => prev.filter((r) => r.id !== id));
      setTotal((t) => Math.max(0, t - 1));
      setActionMsg("Registration deleted");
      setTimeout(() => setActionMsg(""), 2500);
      if (viewing?.id === id) setViewing(null);
    } catch (e) {
      alert("Failed to delete: " + formatApiError(e));
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (category !== "all") params.set("category", category);
      if (status !== "all") params.set("status", status);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (dateFrom || dateTo) params.set("date_field", dateField);
      const token = localStorage.getItem("kumbh_admin_token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/registrations/export?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `registrations_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("Export failed: " + e.message);
    }
  };

  return (
    <div className="space-y-5" data-testid="registrations-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Eczar, serif" }}>
            Registrations
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {total} total registration{total !== 1 ? "s" : ""}
            {(search || category !== "all" || status !== "all" || dateFrom || dateTo) && " (filtered)"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchData}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700 disabled:opacity-60"
            data-testid="refresh-registrations"
          >
            <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-[#FF9933] hover:bg-[#E67300] text-white rounded-md transition-colors font-medium"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {actionMsg && (
        <div
          className="px-4 py-2.5 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm flex items-center gap-2"
          data-testid="action-message"
        >
          <Check className="w-4 h-4" />
          {actionMsg}
        </div>
      )}
      {error && (
        <div className="px-4 py-2.5 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
          <Filter className="w-3.5 h-3.5" />
          Filters
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <form onSubmit={handleSearch} className="sm:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search name, email, phone, or ID..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none"
                data-testid="search-input"
              />
            </div>
          </form>
          <select
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none bg-white capitalize"
            data-testid="filter-category"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c === "all" ? "All Categories" : c}
              </option>
            ))}
          </select>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none bg-white capitalize"
            data-testid="filter-status"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s === "all" ? "All Statuses" : s}
              </option>
            ))}
          </select>
        </div>

        {/* Date Range */}
        <div className="mt-3 pt-3 border-t border-gray-100" data-testid="date-range-filter">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Date Range
            </span>
            <div className="inline-flex rounded-md border border-gray-200 overflow-hidden text-[11px]" data-testid="date-field-toggle">
              <button
                type="button"
                onClick={() => {
                  setDateField("created_at");
                  setPage(1);
                }}
                className={`px-2 py-0.5 transition-colors ${
                  dateField === "created_at"
                    ? "bg-[#FF9933] text-white"
                    : "bg-white text-gray-500 hover:bg-gray-50"
                }`}
                data-testid="date-field-created"
              >
                Registered
              </button>
              <button
                type="button"
                onClick={() => {
                  setDateField("updated_at");
                  setPage(1);
                }}
                className={`px-2 py-0.5 transition-colors border-l border-gray-200 ${
                  dateField === "updated_at"
                    ? "bg-[#FF9933] text-white"
                    : "bg-white text-gray-500 hover:bg-gray-50"
                }`}
                data-testid="date-field-updated"
              >
                Updated
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
                max={dateTo || undefined}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none bg-white"
                data-testid="date-from"
              />
            </div>
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
                min={dateFrom || undefined}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:border-[#FF9933] focus:ring-2 focus:ring-[#FF9933]/20 outline-none bg-white"
                data-testid="date-to"
              />
            </div>
            <div className="sm:col-span-2 flex flex-wrap items-center gap-1">
              {[
                { label: "Today", days: 0 },
                { label: "Last 7d", days: 6 },
                { label: "Last 30d", days: 29 },
                { label: "Last 90d", days: 89 },
              ].map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => {
                    const today = new Date();
                    const from = new Date();
                    from.setDate(today.getDate() - p.days);
                    const iso = (d) => d.toISOString().slice(0, 10);
                    setDateFrom(iso(from));
                    setDateTo(iso(today));
                    setPage(1);
                  }}
                  className="px-2 py-1 text-[11px] rounded border border-gray-200 text-gray-600 hover:bg-[#FF9933]/10 hover:border-[#FF9933]/40 hover:text-[#FF9933]"
                  data-testid={`date-preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {p.label}
                </button>
              ))}
              {(dateFrom || dateTo) && (
                <button
                  type="button"
                  onClick={() => {
                    setDateFrom("");
                    setDateTo("");
                    setPage(1);
                  }}
                  className="px-2 py-1 text-[11px] rounded text-gray-400 hover:text-red-500"
                  data-testid="date-clear"
                >
                  Clear ×
                </button>
              )}
            </div>
          </div>
          {dateField === "updated_at" && (
            <p className="text-[11px] text-gray-400 mt-2" data-testid="updated-at-hint">
              Shows only registrations whose status was changed in this range.
            </p>
          )}
        </div>

        {(search || category !== "all" || status !== "all" || dateFrom || dateTo) && (
          <button
            onClick={clearFilters}
            className="mt-3 text-xs text-gray-500 hover:text-[#FF9933]"
            data-testid="clear-filters"
          >
            Clear filters ×
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-6 h-6 text-[#FF9933] animate-spin mx-auto" />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-gray-400 text-sm">
            No registrations found.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="registrations-table">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["Name", "Contact", "Category", "State", "Status", "Date", "Actions"].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r, i) => (
                  <tr
                    key={r.id}
                    className="hover:bg-gray-50 transition-colors"
                    data-testid={`registration-row-${i}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{r.name}</div>
                      {r.organization && (
                        <div className="text-xs text-gray-500 mt-0.5">{r.organization}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      <div className="text-xs">{r.email || "—"}</div>
                      <div className="text-xs text-gray-400">{r.phone}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-medium capitalize ${
                          categoryBadge[r.category] || "bg-gray-50 text-gray-600"
                        }`}
                      >
                        {r.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{r.state || "—"}</td>
                    <td className="px-4 py-3">
                      <select
                        value={r.status || "confirmed"}
                        onChange={(e) => handleStatusChange(r.id, e.target.value)}
                        className={`text-xs font-medium border rounded px-2 py-1 capitalize outline-none cursor-pointer ${
                          statusBadge[r.status] || "bg-gray-50 text-gray-700 border-gray-200"
                        }`}
                        data-testid={`status-select-${i}`}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setViewing(r)}
                          className="p-1.5 text-gray-500 hover:text-[#FF9933] hover:bg-orange-50 rounded transition-colors"
                          title="View details"
                          data-testid={`view-btn-${i}`}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(r.id, r.name)}
                          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete"
                          data-testid={`delete-btn-${i}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
            <p className="text-xs text-gray-500">
              Page {page} of {pages} · {total} total
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 text-gray-600 hover:text-[#FF9933] disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="prev-page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={page >= pages}
                className="p-1.5 text-gray-600 hover:text-[#FF9933] disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="next-page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View Detail Modal */}
      {viewing && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setViewing(null)}
          data-testid="detail-modal"
        >
          <div
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-lg font-semibold text-gray-900">Registration Details</h3>
              <button
                onClick={() => setViewing(null)}
                className="p-1 hover:bg-gray-100 rounded"
                data-testid="close-modal"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-3">
              {[
                ["Registration ID", viewing.id],
                ["Name", viewing.name],
                ["Email", viewing.email || "—"],
                ["Phone", viewing.phone],
                ["Category", viewing.category],
                ["State", viewing.state || "—"],
                ["Pilgrim Count", viewing.pilgrim_count || 1],
                ["Organization", viewing.organization || "—"],
                ["Skills", viewing.skills || "—"],
                ["Special Needs", viewing.special_needs || "—"],
                ["Message", viewing.message || "—"],
                ["Status", viewing.status || "—"],
                ["Created At", viewing.created_at ? new Date(viewing.created_at).toLocaleString() : "—"],
              ].map(([label, value]) => (
                <div key={label} className="grid grid-cols-3 gap-3 py-2 border-b border-gray-100 last:border-0">
                  <dt className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {label}
                  </dt>
                  <dd className="col-span-2 text-sm text-gray-900 break-words">{value}</dd>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
